import { useCallback, useEffect, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { formatPoNumber, type PoDeliveryView } from '@servwell/shared';
import { useTRPC } from '../../lib/trpc';
import { idem } from '../../lib/idempotency';
import { fieldErrorOf } from '../../lib/field-error';
import { Sheet } from '../ui/Sheet';
import { Button, ghostButton } from '../ui/Button';
import { Notice } from '../ui/Badge';
import { Field, field } from '../create/Field';
import { useOnline } from '../ui/useConnection';

/** The office reviews a frozen supplier copy. Queued email and outside assertions are separate. */
export function PoSendSheet({
  purchaseOrderId,
  poNumber,
  resolution,
  onClose,
}: {
  purchaseOrderId: string;
  poNumber: number;
  resolution?: { delivery: PoDeliveryView; expectedRevision: string };
  onClose: () => void;
}) {
  const trpc = useTRPC(),
    qc = useQueryClient(),
    online = useOnline(),
    started = useRef(false);
  const resolving = Boolean(resolution),
    [channel, setChannel] = useState<'email' | 'manual'>('email');
  const [to, setTo] = useState<string | null>(null),
    [method, setMethod] = useState(''),
    [note, setNote] = useState('');
  const [downloading, setDownloading] = useState(false),
    [downloaded, setDownloaded] = useState(false),
    [downloadError, setDownloadError] = useState<string | null>(null);
  const done = () => {
    void qc.invalidateQueries();
    onClose();
  };
  const prepare = useMutation(
    trpc.purchasing.prepareSend.mutationOptions({
      networkMode: 'always',
      retry: false,
    }),
  );
  const send = useMutation(
    trpc.purchasing.send.mutationOptions({
      networkMode: 'always',
      retry: false,
      onSuccess: done,
    }),
  );
  const manual = useMutation(
    trpc.purchasing.markSentManually.mutationOptions({
      networkMode: 'always',
      retry: false,
      onSuccess: done,
    }),
  );
  const resolve = useMutation(
    trpc.purchasing.resolveSentOutside.mutationOptions({
      networkMode: 'always',
      retry: false,
      onSuccess: done,
    }),
  );
  const [preparation, setPreparation] = useState<{
    pending: boolean;
    copy: typeof prepare.data;
    error: Error | null;
  }>({ pending: !resolving, copy: undefined, error: null });
  const prepareAsync = prepare.mutateAsync;
  // StrictMode replays mount effects and unsubscribes the mutation observer in between.
  // Keep this one automatic intent's result through its promise; do not mint a second
  // mutation merely to reconnect an observer, or leave the sheet showing its old pending state.
  const prepareNow = useCallback(async () => {
    setPreparation({ pending: true, copy: undefined, error: null });
    try {
      const copy = await prepareAsync({ purchaseOrderId, ...idem() });
      setPreparation({ pending: false, copy, error: null });
      setDownloaded(false);
      setDownloadError(null);
      if (!copy.sending.configured) setChannel('manual');
    } catch (error) {
      setPreparation({
        pending: false,
        copy: undefined,
        error:
          error instanceof Error ? error : Error('Could not prepare the supplier copy.'),
      });
    }
  }, [purchaseOrderId, prepareAsync]);
  useEffect(() => {
    if (!resolving && !started.current) {
      started.current = true;
      void prepareNow();
    }
  }, [purchaseOrderId, resolving, prepareNow]);
  const copy = preparation.copy,
    delivery = resolution?.delivery;
  const busy =
    preparation.pending ||
    send.isPending ||
    manual.isPending ||
    resolve.isPending ||
    downloading;
  const pdfUrl =
    resolving && delivery?.documentId
      ? `/api/documents/purchase-order/${delivery.documentId}`
      : copy?.pdfUrl;
  const filename = `${formatPoNumber(poNumber)}.pdf`;
  const error = preparation.error ?? send.error ?? manual.error ?? resolve.error;
  const canEmail =
    online &&
    !busy &&
    Boolean(copy?.sending.configured) &&
    Boolean((to ?? copy?.to ?? '').trim());
  const canConfirm =
    online &&
    !busy &&
    Boolean(method.trim() && note.trim()) &&
    (resolving
      ? Boolean(delivery?.canResolve && resolution?.expectedRevision)
      : Boolean(copy && downloaded));
  const preparedInput = () =>
    copy
      ? {
          purchaseOrderId,
          documentId: copy.documentId,
          sourceHash: copy.sourceHash,
          expectedRevision: copy.expectedRevision,
          ...idem(),
        }
      : null;
  const download = async () => {
    if (!pdfUrl || busy || !online) return;
    setDownloading(true);
    setDownloadError(null);
    try {
      const response = await fetch(pdfUrl, {
        credentials: 'same-origin',
        cache: 'no-store',
      });
      if (
        !response.ok ||
        !response.headers.get('content-type')?.includes('application/pdf')
      )
        throw Error(
          'Could not download the supplier copy. Try again before confirming it was sent.',
        );
      const bytes = await response.arrayBuffer();
      if (new TextDecoder().decode(bytes.slice(0, 5)) !== '%PDF-')
        throw Error('Could not read the supplier copy. Try again.');
      const url = URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' })),
        anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = filename;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      setDownloaded(true);
    } catch (err) {
      setDownloadError(
        err instanceof Error ? err.message : 'Could not download the supplier copy.',
      );
    } finally {
      setDownloading(false);
    }
  };
  const confirm = () => {
    if (!canConfirm) return;
    if (resolution) {
      const d = resolution.delivery;
      if (!d.emailId || !d.documentId || !d.emailStateHash) return;
      resolve.mutate({
        purchaseOrderId,
        emailId: d.emailId,
        documentId: d.documentId,
        expectedEmailStateHash: d.emailStateHash,
        expectedRevision: resolution.expectedRevision,
        method,
        note,
        ...idem(),
      });
    } else {
      const input = preparedInput();
      if (input) manual.mutate({ ...input, method, note });
    }
  };
  return (
    <Sheet
      open
      wide
      onClose={() => {
        if (!busy) onClose();
      }}
      title={
        resolving
          ? `Confirm ${formatPoNumber(poNumber)} outside ServWell`
          : `Send ${formatPoNumber(poNumber)} to vendor`
      }
      footer={
        resolving || channel === 'manual' ? (
          <Button className="w-full" disabled={!canConfirm} onClick={confirm}>
            {busy
              ? 'Saving…'
              : resolving
                ? 'Confirm supplier has this order'
                : 'I sent this order myself'}
          </Button>
        ) : (
          <Button
            className="w-full"
            disabled={!canEmail}
            onClick={() => {
              const input = preparedInput();
              if (input && canEmail) send.mutate({ ...input, to: to ?? copy?.to ?? '' });
            }}
          >
            {busy ? 'Queueing…' : 'Send vendor email'}
          </Button>
        )
      }
    >
      <div className="min-w-0 space-y-4">
        {error ? <Notice tone="danger">{fieldErrorOf(error.message)}</Notice> : null}
        {downloadError ? <Notice tone="danger">{downloadError}</Notice> : null}
        {!online ? (
          <Notice tone="warn">Go online to continue. Your inputs are kept.</Notice>
        ) : null}
        {resolving ? (
          <Notice tone="warn">
            The earlier email was not confirmed. Check with the supplier first. This
            records your confirmation that the supplier has the original order; it sends
            no new email.
          </Notice>
        ) : null}
        {preparation.pending ? <p role="status">Preparing the supplier copy…</p> : null}
        {!resolving && preparation.error ? (
          <Button
            variant="ghost"
            disabled={busy || !online}
            onClick={() => void prepareNow()}
          >
            Prepare current copy
          </Button>
        ) : null}
        {copy || resolving ? (
          <>
            {!resolving ? (
              <div className="flex flex-wrap gap-2">
                <Button
                  variant="ghost"
                  aria-pressed={channel === 'email'}
                  disabled={busy}
                  onClick={() => setChannel('email')}
                >
                  Email vendor
                </Button>
                <Button
                  variant="ghost"
                  aria-pressed={channel === 'manual'}
                  disabled={busy}
                  onClick={() => setChannel('manual')}
                >
                  I sent it myself
                </Button>
              </div>
            ) : null}
            {copy ? (
              <>
                {!copy.sending.configured ? (
                  <Notice tone="warn">
                    Vendor email is not configured here. Download the copy and send it
                    outside ServWell.
                  </Notice>
                ) : (
                  <p className="text-sm text-muted-foreground">
                    Replies go to{' '}
                    {copy.sending.replyTo ??
                      'a confirmed shop reply address you still need to set'}
                    {!copy.sending.ownerConfirmed
                      ? '. Email waits until a shop owner confirms their address.'
                      : '.'}
                  </p>
                )}
                {!copy.sending.replyReady && copy.sending.configured ? (
                  <Notice tone="warn">
                    This email waits for a confirmed reply address.{' '}
                    <a
                      className="underline"
                      href="/settings#email"
                      target="_blank"
                      rel="noreferrer"
                    >
                      Open Email settings
                    </a>
                    .
                  </Notice>
                ) : null}
                {channel === 'email' ? (
                  <Field label="Vendor email for this send">
                    <input
                      className={field}
                      type="email"
                      aria-label="Vendor email for this send"
                      value={to ?? copy.to ?? ''}
                      maxLength={254}
                      disabled={busy}
                      onChange={(e) => setTo(e.target.value)}
                      autoComplete="email"
                    />
                    <span className="mt-1 block text-muted-foreground">
                      Changing this address does not change the vendor.
                    </span>
                  </Field>
                ) : null}
              </>
            ) : null}
            {pdfUrl ? (
              <div className="flex flex-wrap gap-2">
                <a className={ghostButton} href={pdfUrl} target="_blank" rel="noreferrer">
                  {resolving ? 'View original supplier copy' : 'View supplier copy'}
                </a>
                <Button
                  variant="ghost"
                  disabled={busy || !online}
                  onClick={() => void download()}
                >
                  {downloading
                    ? 'Downloading…'
                    : downloaded
                      ? 'Download copy again'
                      : 'Download supplier copy'}
                </Button>
              </div>
            ) : null}
            {resolving || channel === 'manual' ? (
              <fieldset disabled={busy} className="min-w-0 space-y-3">
                <p className="text-sm text-muted-foreground">
                  {resolving
                    ? 'Confirm only after checking the original order with the supplier.'
                    : 'Download this copy, then send it outside ServWell before confirming below. This records your assertion that you sent it.'}
                </p>
                <Field label={resolving ? 'How you confirmed' : 'How you sent it'}>
                  <input
                    className={field}
                    value={method}
                    maxLength={200}
                    onChange={(e) => setMethod(e.target.value)}
                    placeholder="Phone, email, or in person"
                  />
                </Field>
                <Field label="Confirmation note">
                  <textarea
                    aria-label="Confirmation note"
                    className={`${field} min-h-20 py-2`}
                    value={note}
                    maxLength={2000}
                    onChange={(e) => setNote(e.target.value)}
                  />
                </Field>
                {!resolving && !downloaded ? (
                  <p role="status" className="text-sm text-muted-foreground">
                    Download the supplier copy before confirming it was sent.
                  </p>
                ) : null}
              </fieldset>
            ) : null}
            {copy ? (
              <div className="min-w-0 space-y-2">
                <p className="text-sm font-semibold">{copy.subject}</p>
                <details open>
                  <summary className="cursor-pointer text-sm font-medium">
                    Review email text
                  </summary>
                  <pre className="mt-2 max-h-64 overflow-y-auto whitespace-pre-wrap break-words rounded-[var(--radius)] border border-border bg-surface-2 p-3 font-sans text-sm">
                    {copy.body}
                  </pre>
                </details>
                <p className="text-xs text-muted-foreground">
                  The email includes this supplier copy as a PDF. The order stays Draft
                  while queued; Ordered means the mail provider accepted it.
                </p>
              </div>
            ) : null}
            {!resolving && error ? (
              <Button
                variant="ghost"
                disabled={busy || !online}
                onClick={() => {
                  send.reset();
                  manual.reset();
                  setDownloaded(false);
                  void prepareNow();
                }}
              >
                Prepare current copy (replaces preview)
              </Button>
            ) : null}
          </>
        ) : null}
      </div>
    </Sheet>
  );
}
